/**
 * The Frame picker's localStorage layer — FRAME_PICKER_DESIGN.md §6.4 `state/persistence.ts`.
 *
 * TWO KEYS, ONE NAMESPACE. `StorageService` writes `<prefix>-<key>` (`services/storage.service.ts:49`),
 * so with the prefix `FRAMEPICKER`:
 *   FRAMEPICKER-<sid>              the record: sid → workflow ids, the chosen times, the mode
 *   FRAMEPICKER-LATEST-<pageSlug>  which sid this page was last editing
 * `sid` is `[A-Za-z0-9_-]{1,64}` and may not start with `LATEST-`, so the two shapes never collide
 * and the sweep's key filter (§6.4: "a key-shape filter — the sweep would otherwise return the
 * LATEST keys") is a real predicate, not a hope. The URL carries the same id as `psid`.
 *
 * NEVER BYTES (§6.4): "never a `File`, blob URL or tile". The record holds the staged file's
 * identity (`uuid`, name, size) so a restore can find it again in `host.stagedFiles$`; a spec
 * asserts the serialised record contains neither `blob:` nor `data:`. ONE bounded exception since
 * R14 (slice H, ask 10): `thumbs` -- a few small JPEG data URLs of the frames Convert was pressed
 * for, so the /downloads card can show the picked frame before the edge has a thumbnail. Capped in
 * count (`MAX_THUMBS`), per picture (`MAX_THUMB_CHARS`) and in total (`MAX_THUMBS_TOTAL_CHARS`, the
 * oldest dropped first -- Sr review 2 SHOULD 3), validated on read, never load-bearing.
 *
 * HONESTY. `StorageService.store` swallows its exception and returns `null` (`:47-57`), and its
 * constructor falls back to an in-memory object when `localStorage` throws (`:26-42`) — so a
 * write that reached no disk can look saved. Every write here READS BACK and compares, and
 * `storageHealth()` is a write-then-read probe (§6.4: "a write-then-read check reports `disabled`").
 *
 * VERSIONED, TOLERANT OF GARBAGE. An unknown `v`, a non-object, a missing `sid`, a `savedAt` that
 * is not a number — every one reads as ABSENT (null), never as a throw and never as a migration.
 * Field-level garbage inside a record of the right version is dropped field by field.
 *
 * NO WALL-CLOCK, NO DOM IN THE PURE PARTS. The storage backend and `now` are parameters; the
 * browser adapter (`browserStorage()`) is the only function that touches `window`, and it
 * resolves `StorageService` from `context/container` at CALL time, never with `@inject`, for the
 * reason `utils/metric-emit.ts` gives (a decoration-time service reference TDZ-crashes the SSG
 * build while leaving jest green).
 */
import { container } from '../../../context/container';
import { StorageService } from '../../../services/storage.service';
import { frameStart, type Timeline } from '../media/timeline';
import type { PickerMode, PickerState, SheetSettings, Spacing, TimeRange } from './picker-state';
import { DEFAULT_SHEET, FRAMEPICKER_TTL_MS, FRAME_LIMITS, INTERVAL_STEPS, MAX_PINS, PIN_EPSILON } from './picker-state';

export const FRAMEPICKER_PREFIX = 'FRAMEPICKER';
export const FRAMEPICKER_RECORD_VERSION = 1;

/** §6.4 said "swept 8 h after `savedAt` on open"; IT1 (saas #8 / qa #8): 2.5 h, ONE number with the Studio and the edge (`state/picker-state.ts`). */
export { FRAMEPICKER_TTL_MS, FRAMEPICKER_TTL_HOURS } from './picker-state';

/** §6.4: "debounced 750 ms + `pagehide`". The root owns the timer; the number lives here. */
export const AUTOSAVE_DEBOUNCE_MS = 750;

const LATEST_PREFIX = 'LATEST-';

/** Deliberately NOT under `FRAMEPICKER-`, so the sweep can never be asked to reason about it. */
const PROBE_KEY = '__xc_framepicker_probe__';

const SID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function isValidSid(sid: unknown): sid is string {
    return typeof sid === 'string' && SID_RE.test(sid) && !sid.startsWith(LATEST_PREFIX);
}

/**
 * The staged file's identity — enough to find it again in `host.stagedFiles$` — and, since round 3
 * (ROUND3_BRIEF.md R9a), enough to RE-ADDRESS the clip on the server when it is not staged any
 * more: Convert routes to `/downloads`, which unmounts the page and with it the in-memory `File`,
 * so Back used to land in the expired state with the mode and the playhead restored around an
 * empty stage. `location` (the 24-hex upload id inside the safe name) + `token` + `uploadDomain`
 * are exactly what `root/source.ts#renditionUrlFrom` composes the `preview` rendition from;
 * `processorMetadata` is the uploader's own processor record for the file (domains, endpoints,
 * safe name), which is what `FileUploadUtils.toUploadInProgressFile` puts back on a recovered
 * upload so it can be converted again without a second upload; the probe facts save a round trip.
 * Still never the bytes: no `File`, no blob URL, no tile. Every new field is optional, so a record
 * written before this shape reads exactly as it did.
 */
export interface StagedFileRef {
    /** `UploadInProgressFile.uuid`. */
    uuid: string;
    name: string;
    size: number;
    /** `OriginalInputFile.location` — the safe name carrying the upload id. */
    location?: string;
    /** `OriginalInputFile.token` — the upload JWT the rendition route is addressed with. */
    token?: string;
    /** `ProcessorMetadata.uploadDomain` — the edge that holds the file. */
    uploadDomain?: string;
    /** The whole `ProcessorMetadata` for the upload, JSON-safe and opaque here. */
    processorMetadata?: Record<string, unknown>;
    /** `UploadInProgressFile.uniqueProcessType` (the page's), for a re-conversion. */
    uniqueProcessType?: string;
    /** The file's MIME type, for the stub `File`. */
    type?: string;
    /** The probe's answer (`/files/input/info`), so a restore paints without asking again. */
    duration?: number;
    fps?: number;
    width?: number;
    height?: number;
    /**
     * R14 (slice L's BUG-016 / BUG-015, R14-SRV5; written by slice H): the probe's frame facts beyond the nominal rate -- the frames really
     * present (`frame_count`), the timestamp-derived rate (`avg_framerate`), the server's CFR / VFR verdict (`frame_rate_mode`, the one
     * spelling the /downloads card reads) and the DISPLAY size (`display_width` × `display_height`, coded × the sample aspect ratio) --
     * so a restored VFR clip stays VFR and an anamorphic one keeps its shape without asking the edge again. All optional.
     */
    frameCount?: number;
    avgFps?: number;
    frameRateMode?: 'CFR' | 'VFR';
    displayWidth?: number;
    displayHeight?: number;
}

/** Can a record's file be re-addressed on the server (the preview rendition and a re-conversion)? */
export function canRestoreFile(ref: StagedFileRef | null | undefined): ref is StagedFileRef & { location: string; token: string } {
    return !!ref && typeof ref.location === 'string' && ref.location !== '' && typeof ref.token === 'string' && ref.token !== '';
}

export interface PickerRecord {
    v: number;
    sid: string;
    pageSlug: string;
    file: StagedFileRef | null;
    /** The workflows this sid's Convert produced (§6.1: "records sid → workflow before `Router.push('/downloads')`"). */
    workflowIds: string[];
    mode: PickerMode;
    t: number;
    /** `end: null` = open-ended (the reducer's `+∞`, which JSON cannot carry). */
    range: { start: number; end: number | null } | null;
    spacing: Spacing;
    interval: number;
    count: number;
    sheet: SheetSettings;
    savedAt: number;
    /** Bumped on every save; a lower one never overwrites a higher one (two tabs, §6.4). */
    generation: number;
    /** Round 12 (N3, P4): the pinned frames' times and whether Picked frames is what you get. Optional: older records read as none. */
    pins?: number[];
    picked?: boolean;
    /**
     * IT2 (Y2, qa N1): the frame time the USER chose for every ONE-frame job this sid made, keyed by the instant the job
     * asked ffmpeg for (`seekKey` of the written `screenshotTime`: "1.55" for a typed 1.567 whose frame starts at 1.5667).
     * The request keeps the seek instant (IT1 qa #2: `-ss` delivers the first frame with pts ≥ t, so the write is half a
     * frame early); the /downloads card reads the badge off the request and would name a time the user never chose
     * ("Frame at 0:01.550") -- `userFrameTimeOf` gives it the user's. Optional: older records read as none.
     */
    frameTimes?: Record<string, number>;
    /**
     * R14 (slice J, BUG-024 / review-6 BUG-043): EVERY staged video's ref, in the Files pane's order -- not only the
     * selected one (`file`). Convert routes to `/downloads` and the page forgets its list; Back used to rebuild the
     * one clip the record named and silently dropped the others (`files=2` → `files=1`). The root rebuilds each ref
     * that is not staged any more (`root/source.ts#missingStagedRefs`) or names the ones that expired. Optional: an
     * older record reads as none, and a save that does not carry the list keeps the stored one (`saveRecord`).
     */
    staged?: StagedFileRef[];
    /**
     * R14 (slice J, BUG-026 / review-6 BUG-045): the conversions this sid started, written AT THE PRESS (before the
     * schedule POST answers) and completed with the workflow id when the edge answers -- so a user who navigated away
     * during the progress screen comes back to a record that knows the job, and the root can say so instead of
     * "Ready" over an idle editor that invites a duplicate. Newest last; capped at `MAX_JOBS`. Optional.
     */
    jobs?: RecordedJob[];
    /**
     * R14 (slice H, investigation K ask 10 / R14U-05): the picture of the frame each ONE-frame job was pressed for -- a small
     * JPEG data URL (`data:image/jpeg;base64,…`, ≤ `MAX_THUMB_CHARS`) taken from the Output canvas (or the pin's own snapshot)
     * AT THE PRESS, so the /downloads card can show the frame the user picked before the edge has rendered its own thumbnail
     * (`frame-picker-card.ts` reads it through `thumbFor` on the parsed record; the edge URL replaces it once the result lands).
     * Sr review 2 (SHOULD 3/4 + the parent's per-file ruling): BY FILE (`jobs[].fileUuid` → this key -- two clips pressed at the
     * same seek no longer share one picture), each file's list in PRESS ORDER (`at`, oldest first -- never the object's key order,
     * which sorts integer-like seeks first), one entry per (file, seek) with the newest winning; the newest `MAX_THUMBS` across
     * the record within `MAX_THUMBS_TOTAL_CHARS`. A save that does not carry the map keeps the stored one (bookkeeping, like
     * `frameTimes`). Optional: older records read as none; the pre-review flat `{ seek: url }` shape is dropped on read.
     */
    thumbs?: Record<string, ThumbEntry[]>;
}

/** R14 (slice H, ask 10; Sr review 2 SHOULD 4): one pressed frame's picture -- the wire's seek key, the JPEG data URL, the press time. */
export interface ThumbEntry {
    /** `seekKey` of the written `screenshotTime` -- the key the /downloads card looks up. */
    seek: string;
    url: string;
    /** When Convert was pressed (ms since the epoch): the press order across files and writes. */
    at: number;
}

/** R14 (slice H, ask 10; Sr review 2 SHOULD 3): how many thumbnails a record keeps in all (the newest presses). */
export const MAX_THUMBS = 6;
/** R14 (slice H, ask 10): the longest data URL a thumbnail may be (≈ 24 KB; a 320 px JPEG at q 0.7 is 10–20 KB). */
export const MAX_THUMB_CHARS = 24 * 1024;
/** Sr review 2 SHOULD 3: the most picture bytes a record carries in all (≈ 64 KB; the oldest go first) -- every autosave re-parses the record. */
export const MAX_THUMBS_TOTAL_CHARS = 64 * 1024;
/** The only picture shape a record accepts. */
export const THUMB_DATA_URL_PREFIX = 'data:image/jpeg;base64,';

/** R14 (slice J, BUG-026): one conversion this sid asked for. `id` is null until the edge's schedule answer lands. */
export interface RecordedJob {
    /** The workflow id, once known; null while the answer is owed (or for ever, when the page left before it came). */
    id: string | null;
    /** `UploadInProgressFile.uuid` of the clip the job was made from (a `staged` / `file` ref names it). */
    fileUuid: string;
    mode: PickerMode;
    /** ONE: the frame time the USER chose (seconds, exact). Absent for a sequence or a sheet. Picked frames: the first pin. */
    t?: number;
    /**
     * Editor re-open T5 (R-D): a Picked-frames press is ONE job (`SCREENSHOT_PICKED_FRAMES`, one workflow, N files) -- every pin's
     * exact time, ascending, so the record maps the one workflow to all N pins. Absent on every other job (and on the per-pin
     * ONE jobs older records carry). Recorded under mode `ONE` (the tree mode the picker holds while Picked frames is chosen).
     */
    picks?: number[];
    /**
     * R14U-41: the WIRE of the press -- the duplicate guard's fingerprint. Editor re-open T3b: for EVERY mode, the full fingerprint
     * (`jobFingerprintOf`: frame-selection keys + the output option summary). Older records carry the selection-only wire
     * (`export/convert.ts#wireKeyOf`) or none; `sameJobOf` reads those by the old rule.
     */
    wire?: string;
    /** R14Q-56: written with the lifetime rule (guarded until failed / the cap); absent on records from before it (the 60 s rule). */
    guard?: 'lifetime';
    /** R14Q-56: what the edge reported for this job, once something learned it (`recordJobFinish`); absent while it is owed. */
    status?: 'done' | 'failed';
    /** R14Q-56: when `status` landed (ms since the epoch). */
    finishedAt?: number;
    /** When Convert was pressed (`now()`), ms since the epoch. */
    startedAt: number;
}

/** How many jobs a record remembers (the newest); a session that converts more forgets the oldest. */
export const MAX_JOBS = 24;
/** How many staged refs a record carries (the Files pane's first N); the cap bounds the record's size. */
export const MAX_STAGED_REFS = 32;
/** R14 (BUG-026): a job without an id older than this is not "still being scheduled" -- the answer never reached this device. */
export const JOB_PENDING_MS = 60_000;
/**
 * R14Q-56 (QA pass 8, option a): the duplicate guard's window is the JOB'S LIFETIME -- a job that takes 91–150 s let the same press after
 * Back schedule a silent duplicate under the 60 s rule. A lifetime-guarded job (`guard: 'lifetime'`, every job written from now on) stays
 * guarded from the press until it is marked failed, capped here; a job marked done keeps its sentence ("already on Downloads") until the cap.
 * "Convert it again" stays the escape. Records written before this keep the 60 s rule.
 */
export const JOB_LIFETIME_MS = 10 * 60_000;

export type JobStatus =
    /** The edge has the job (its id is known): the card exists on `/downloads`. */
    | 'sent'
    /** Pressed less than `JOB_PENDING_MS` ago and no id yet: the schedule answer is still owed. */
    | 'pending'
    /** Pressed, never answered on this device (the page left before the answer): the job may or may not be on `/downloads`. */
    | 'unknown';

export type WriteResult =
    | { status: 'saved'; record: PickerRecord }
    | { status: 'disabled' }
    /**
     * Another tab wrote this sid's record since we last read it AND its editable content (mode, times, pins, files)
     * differs from ours (R14 slice J, BUG-025): nothing was written. The caller shows the sentence and keeps its own
     * state; it does NOT apply `stored` (that was the silent overwrite). A write that only differs in bookkeeping
     * (workflow ids, frame times, jobs -- a Convert in the other tab, or this tab's own detached wait) is not a conflict:
     * `saveRecord` merges it and saves.
     */
    | { status: 'conflict'; stored: PickerRecord }
    /** A record written by a NEWER bundle is left exactly where it is. */
    | { status: 'foreign-version'; version: number };

/**
 * What persistence needs from a store: JSON-valued reads and writes under the FULL key
 * (`FRAMEPICKER-<sid>`), plus the key list for the sweep. `write` returns whether the read-back
 * matched. The browser adapter and the spec's in-memory fake both implement it.
 */
export interface PickerStorage {
    read(fullKey: string): unknown;
    write(fullKey: string, value: unknown): boolean;
    remove(fullKey: string): void;
    keys(): string[];
}

export function recordKey(sid: string): string {
    return `${FRAMEPICKER_PREFIX}-${sid}`;
}

export function latestKey(pageSlug: string): string {
    return `${FRAMEPICKER_PREFIX}-${LATEST_PREFIX}${pageSlug}`;
}

/** The inverse of `recordKey`, or null for a LATEST key, a foreign key or a malformed sid. */
export function sidFromKey(fullKey: string): string | null {
    const head = `${FRAMEPICKER_PREFIX}-`;
    if (!fullKey.startsWith(head)) return null;
    const sid = fullKey.slice(head.length);
    return isValidSid(sid) ? sid : null;
}

// ---------------------------------------------------------------------------------------------
// The browser adapter (the only DOM in this file).
// ---------------------------------------------------------------------------------------------

function storageService(): StorageService | null {
    if (typeof window === 'undefined') return null;
    try {
        return container.get(StorageService);
    } catch {
        return null;
    }
}

function splitKey(fullKey: string): [string, string] | null {
    // `StorageService` joins `<prefix>-<key>`; only the namespace's own keys go through it. The
    // probe key deliberately has no prefix (see `PROBE_KEY`), so it takes the direct path.
    const head = `${FRAMEPICKER_PREFIX}-`;
    return fullKey.startsWith(head) ? [FRAMEPICKER_PREFIX, fullKey.slice(head.length)] : null;
}

/**
 * `StorageService` is the writer (§6.4 "via `StorageService` with an ad-hoc prefix"); reads and the
 * read-back go to `window.localStorage` directly, because the service's in-memory fallback would
 * report `saved` for a write that reached no disk (see the header).
 */
export function browserStorage(): PickerStorage | null {
    if (typeof window === 'undefined') return null;
    return {
        read(fullKey) {
            try {
                const raw = window.localStorage.getItem(fullKey);
                return raw === null || raw === undefined ? null : JSON.parse(raw);
            } catch {
                return null;
            }
        },
        write(fullKey, value) {
            let expected: string;
            try {
                expected = JSON.stringify(value);
            } catch {
                return false;
            }
            try {
                const svc = storageService();
                const parts = splitKey(fullKey);
                if (svc && parts) svc.store(parts[0], parts[1], value);
                else window.localStorage.setItem(fullKey, expected);
                return window.localStorage.getItem(fullKey) === expected;
            } catch {
                return false;
            }
        },
        remove(fullKey) {
            try {
                const svc = storageService();
                const parts = splitKey(fullKey);
                if (svc && parts) svc.remove(parts[0], parts[1]);
                else window.localStorage.removeItem(fullKey);
            } catch { /* nothing to undo */ }
        },
        keys() {
            try {
                const out: string[] = [];
                for (let i = 0; i < window.localStorage.length; i++) {
                    const k = window.localStorage.key(i);
                    if (k) out.push(k);
                }
                return out;
            } catch {
                return [];
            }
        },
    };
}

/** Write, READ BACK, compare, clean up (§6.4). */
export function storageHealth(storage: PickerStorage | null = browserStorage(), now: () => number = Date.now): 'ok' | 'disabled' {
    if (!storage) return 'disabled';
    try {
        const stamp = `probe-${now()}`;
        const ok = storage.write(PROBE_KEY, stamp);
        const back = storage.read(PROBE_KEY);
        storage.remove(PROBE_KEY);
        return ok && back === stamp ? 'ok' : 'disabled';
    } catch {
        return 'disabled';
    }
}

// ---------------------------------------------------------------------------------------------
// Parsing — tolerant of garbage.
// ---------------------------------------------------------------------------------------------

function isObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function finite(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function parseMode(v: unknown): PickerMode {
    return v === 'ONE' || v === 'SEQUENCE' || v === 'SHEET' ? v : 'ONE';
}

function parseSpacing(v: unknown): Spacing {
    return v === 'BY_COUNT' ? 'BY_COUNT' : 'BY_INTERVAL';
}

function parseRange(v: unknown): PickerRecord['range'] {
    if (!isObject(v)) return null;
    const start = finite(v.start);
    if (start === null || start < 0) return null;
    const end = v.end === null ? null : finite(v.end);
    if (end !== null && end < 0) return null;
    return { start, end };
}

function parseSheet(v: unknown): SheetSettings {
    const sheet: SheetSettings = { ...DEFAULT_SHEET };
    if (!isObject(v)) return sheet;
    const cols = finite(v.columns);
    if (cols !== null) sheet.columns = Math.min(FRAME_LIMITS.maxSheetColumns, Math.max(1, Math.round(cols)));
    const width = finite(v.width);
    if (width !== null) sheet.width = Math.min(FRAME_LIMITS.maxSheetWidthPx, Math.max(FRAME_LIMITS.minSheetWidthPx, Math.round(width)));
    if (typeof v.timestamps === 'boolean') sheet.timestamps = v.timestamps;
    if (typeof v.header === 'boolean') sheet.header = v.header;
    return sheet;
}

function optString(v: unknown): string | undefined {
    return typeof v === 'string' && v !== '' ? v : undefined;
}

function optPositive(v: unknown): number | undefined {
    const n = finite(v);
    return n !== null && n > 0 ? n : undefined;
}

function parseFileRef(v: unknown): StagedFileRef | null {
    if (!isObject(v) || typeof v.uuid !== 'string' || v.uuid === '') return null;
    const ref: StagedFileRef = {
        uuid: v.uuid,
        name: typeof v.name === 'string' ? v.name : '',
        size: finite(v.size) ?? 0,
    };
    // R9a: the re-addressing fields, each dropped on its own when it is garbage.
    const location = optString(v.location);
    const token = optString(v.token);
    const uploadDomain = optString(v.uploadDomain);
    const uniqueProcessType = optString(v.uniqueProcessType);
    const type = optString(v.type);
    if (location) ref.location = location;
    if (token) ref.token = token;
    if (uploadDomain) ref.uploadDomain = uploadDomain;
    if (isObject(v.processorMetadata)) ref.processorMetadata = { ...v.processorMetadata };
    if (uniqueProcessType) ref.uniqueProcessType = uniqueProcessType;
    if (type) ref.type = type;
    const duration = optPositive(v.duration);
    const fps = optPositive(v.fps);
    const width = optPositive(v.width);
    const height = optPositive(v.height);
    if (duration) ref.duration = duration;
    if (fps) ref.fps = fps;
    if (width) ref.width = width;
    if (height) ref.height = height;
    // R14 (slice L's facts): each dropped alone when it is garbage; a mode other than the two words is not a mode.
    const frameCount = optPositive(v.frameCount);
    const avgFps = optPositive(v.avgFps);
    const displayWidth = optPositive(v.displayWidth);
    const displayHeight = optPositive(v.displayHeight);
    if (frameCount) ref.frameCount = Math.round(frameCount);
    if (avgFps) ref.avgFps = avgFps;
    if (v.frameRateMode === 'CFR' || v.frameRateMode === 'VFR') ref.frameRateMode = v.frameRateMode;
    if (displayWidth) ref.displayWidth = displayWidth;
    if (displayHeight) ref.displayHeight = displayHeight;
    return ref;
}

/** A well-formed record of THIS version, or null. Never throws. */
export function parseRecord(raw: unknown): PickerRecord | null {
    if (!isObject(raw)) return null;
    if (raw.v !== FRAMEPICKER_RECORD_VERSION) return null;
    if (!isValidSid(raw.sid)) return null;
    const savedAt = finite(raw.savedAt);
    if (savedAt === null) return null;
    const interval = finite(raw.interval);
    const count = finite(raw.count);
    const t = finite(raw.t);
    // Round 12 (P4): finite, non-negative, distinct times only, in time order, at most MAX_PINS; `picked` needs a pin.
    // R14 (BUG-001): pins are read back EXACT -- rounding them to the millisecond here put a pin at `1.43333…` (frame 43)
    // at `1.433`, inside frame 42, on every restore. Two within `PIN_EPSILON` are one pin (the reducer's rule).
    const pins = Array.isArray(raw.pins)
        ? raw.pins.filter((p): p is number => typeof p === 'number' && Number.isFinite(p) && p >= 0).sort((a, b) => a - b)
            .filter((p, i, all) => i === 0 || Math.abs(p - all[i - 1]) >= PIN_EPSILON).slice(0, MAX_PINS)
        : [];
    const picked = raw.picked === true && pins.length > 0;
    const frameTimes = parseFrameTimes(raw.frameTimes);
    const staged = parseStagedRefs(raw.staged);
    const jobs = parseJobs(raw.jobs);
    const thumbs = parseThumbs(raw.thumbs);
    return {
        v: FRAMEPICKER_RECORD_VERSION,
        sid: raw.sid,
        pageSlug: typeof raw.pageSlug === 'string' ? raw.pageSlug : '',
        file: parseFileRef(raw.file),
        workflowIds: Array.isArray(raw.workflowIds) ? raw.workflowIds.filter((w): w is string => typeof w === 'string' && w !== '') : [],
        mode: parseMode(raw.mode),
        t: t !== null && t >= 0 ? t : 0,
        range: parseRange(raw.range),
        spacing: parseSpacing(raw.spacing),
        interval: interval !== null && INTERVAL_STEPS.some((s) => Math.abs(s.seconds - interval) < 1e-9) ? interval : 1,
        count: count !== null && Number.isInteger(count) && count >= FRAME_LIMITS.minCount ? count : 12,
        sheet: parseSheet(raw.sheet),
        savedAt,
        generation: Math.max(0, Math.floor(finite(raw.generation) ?? 0)),
        pins,
        picked,
        ...(frameTimes ? { frameTimes } : {}),
        ...(staged ? { staged } : {}),
        ...(jobs ? { jobs } : {}),
        ...(thumbs ? { thumbs } : {}),
    };
}

/** R14 (slice H, ask 10): is this a JPEG data URL a record may hold -- the one prefix, base64 body, within the size cap? */
export function isThumbDataUrl(v: unknown): v is string {
    return typeof v === 'string' && v.length <= MAX_THUMB_CHARS && v.startsWith(THUMB_DATA_URL_PREFIX)
        && v.length > THUMB_DATA_URL_PREFIX.length && /^[A-Za-z0-9+/]+=*$/.test(v.slice(THUMB_DATA_URL_PREFIX.length));
}

/**
 * R14 (slice H, ask 10; Sr review 2 SHOULD 3/4): `{ "<fileUuid>": [{ seek: "1.55", url: "data:image/jpeg;base64,…", at: 1700… }] }`
 * -- an entry whose seek is not a seek, whose picture is not a small JPEG data URL or whose press time is not a number drops
 * alone; a file key that is not a list (the pre-review flat shape) drops whole. Then the BUDGET (`thumbBudget`).
 */
function parseThumbs(v: unknown): Record<string, ThumbEntry[]> | null {
    if (!isObject(v)) return null;
    const all: FiledThumb[] = [];
    for (const [file, list] of Object.entries(v)) {
        if (!file || !Array.isArray(list)) continue;
        for (const e of list) {
            if (!isObject(e)) continue;
            const seek = typeof e.seek === 'string' || typeof e.seek === 'number' ? seekKey(e.seek) : null;
            const at = finite(e.at);
            if (seek === null || at === null || !isThumbDataUrl(e.url)) continue;
            all.push({ file, seek, url: e.url, at });
        }
    }
    return thumbBudget(all);
}

interface FiledThumb extends ThumbEntry { file: string }

/**
 * One entry per (file, seek) -- the newest press wins; press order (`at`, ties by arrival) across every file; the newest
 * `MAX_THUMBS` kept, then the oldest dropped while the pictures together exceed `MAX_THUMBS_TOTAL_CHARS`. Null when nothing is left.
 */
function thumbBudget(all: readonly FiledThumb[]): Record<string, ThumbEntry[]> | null {
    const byKey = new Map<string, FiledThumb>();
    for (const e of all) {
        const k = `${e.file}|${e.seek}`;
        const have = byKey.get(k);
        if (have && e.at < have.at) continue;
        if (have) byKey.delete(k); // the newer press takes the later place (press order on a tie = arrival order)
        byKey.set(k, e);
    }
    const kept = Array.from(byKey.values()).sort((a, b) => a.at - b.at).slice(-MAX_THUMBS);
    const size = () => kept.reduce((n, e) => n + e.url.length, 0);
    while (kept.length > 0 && size() > MAX_THUMBS_TOTAL_CHARS) kept.shift();
    if (!kept.length) return null;
    const out: Record<string, ThumbEntry[]> = {};
    for (const { file, seek, url, at } of kept) (out[file] ??= []).push({ seek, url, at });
    return out;
}

/** The record's map flattened, in its stored order (each file's list is already oldest-first). */
function thumbEntries(thumbs: Record<string, ThumbEntry[]> | undefined): FiledThumb[] {
    const out: FiledThumb[] = [];
    for (const [file, list] of Object.entries(thumbs ?? {})) for (const e of list ?? []) out.push({ file, ...e });
    return out;
}

/** R14 (BUG-024): the staged refs, each parsed as `file` is, one entry per uuid, the first `MAX_STAGED_REFS`; null for none. */
function parseStagedRefs(v: unknown): StagedFileRef[] | null {
    if (!Array.isArray(v)) return null;
    const out: StagedFileRef[] = [];
    const seen = new Set<string>();
    for (const item of v) {
        const ref = parseFileRef(item);
        if (!ref || seen.has(ref.uuid)) continue;
        seen.add(ref.uuid);
        out.push(ref);
        if (out.length >= MAX_STAGED_REFS) break;
    }
    return out.length ? out : null;
}

/** R14 (BUG-026): a job needs a file uuid and a finite `startedAt`; `id` is a non-empty string or null; garbage rows drop one by one. */
function parseJobs(v: unknown): RecordedJob[] | null {
    if (!Array.isArray(v)) return null;
    const out: RecordedJob[] = [];
    for (const item of v) {
        if (!isObject(item) || typeof item.fileUuid !== 'string' || item.fileUuid === '') continue;
        const startedAt = finite(item.startedAt);
        if (startedAt === null) continue;
        const job: RecordedJob = { id: optString(item.id) ?? null, fileUuid: item.fileUuid, mode: parseMode(item.mode), startedAt };
        const t = finite(item.t);
        if (t !== null && t >= 0) job.t = t;
        const wire = optString(item.wire);
        if (wire) job.wire = wire;
        if (Array.isArray(item.picks)) {
            const picks = item.picks.map(finite).filter((p): p is number => p !== null && p >= 0);
            if (picks.length) job.picks = picks.slice(0, MAX_PINS);
        }
        if (item.guard === 'lifetime') job.guard = 'lifetime';
        if (item.status === 'done' || item.status === 'failed') job.status = item.status;
        const finishedAt = finite(item.finishedAt);
        if (finishedAt !== null && job.status) job.finishedAt = finishedAt;
        out.push(job);
    }
    return out.length ? out.slice(-MAX_JOBS) : null;
}

/** IT2 (qa N1): `{ "1.55": 1.567 }` -- finite non-negative seconds under seek keys; garbage entries drop one by one. */
function parseFrameTimes(v: unknown): Record<string, number> | null {
    if (!isObject(v)) return null;
    const out: Record<string, number> = {};
    for (const [k, t] of Object.entries(v)) {
        const key = seekKey(k);
        const secs = finite(t);
        if (key !== null && secs !== null && secs >= 0) out[key] = secs;
    }
    return Object.keys(out).length ? out : null;
}

/** The version of a stored blob, if it has one — for the `foreign-version` result. */
function storedVersion(raw: unknown): number | null {
    return isObject(raw) ? finite(raw.v) : null;
}

// ---------------------------------------------------------------------------------------------
// State ↔ record.
// ---------------------------------------------------------------------------------------------

/** The persisted half of `PickerState` (§6.4 lists the fields; `zoom`, `source`, `step` are session-only). */
export function toRecord(
    state: PickerState,
    extras: {
        pageSlug: string; file: StagedFileRef | null; workflowIds?: string[]; generation?: number; frameTimes?: Record<string, number>;
        /** R14 (BUG-024): every staged video's ref (`export/convert.ts#stagedRefsOf`). Omitted (undefined) = "I do not know the list": `saveRecord` keeps the stored one. */
        staged?: StagedFileRef[] | null;
        /** R14 (BUG-026): the jobs this sid started, when the caller carries them; omitted = keep the stored ones. */
        jobs?: RecordedJob[] | null;
        /** R14 (slice H, ask 10): the pressed frames' pictures by file, when the caller carries them; omitted = keep the stored ones. */
        thumbs?: Record<string, ThumbEntry[]> | null;
    },
    now: () => number,
): PickerRecord {
    const range: PickerRecord['range'] = state.range
        ? { start: state.range.start, end: Number.isFinite(state.range.end) ? state.range.end : null }
        : null;
    return {
        v: FRAMEPICKER_RECORD_VERSION,
        sid: state.sid,
        pageSlug: extras.pageSlug,
        file: extras.file,
        workflowIds: [...(extras.workflowIds ?? [])],
        mode: state.mode,
        t: state.t,
        range,
        spacing: state.spacing,
        interval: state.interval,
        count: state.count,
        sheet: { ...state.sheet },
        savedAt: now(),
        generation: extras.generation ?? 0,
        pins: [...state.pins],
        picked: state.picked && state.pins.length > 0,
        ...(extras.frameTimes && Object.keys(extras.frameTimes).length ? { frameTimes: { ...extras.frameTimes } } : {}),
        ...(extras.staged ? { staged: extras.staged.slice(0, MAX_STAGED_REFS).map((r) => ({ ...r })) } : {}),
        ...(extras.jobs ? { jobs: extras.jobs.slice(-MAX_JOBS).map((j) => ({ ...j })) } : {}),
        ...(extras.thumbs && parseThumbs(extras.thumbs) ? { thumbs: parseThumbs(extras.thumbs) as Record<string, ThumbEntry[]> } : {}),
    };
}

/** The fields a restore puts back onto a fresh `PickerState` (the rest is session-only). */
export function fromRecord(record: PickerRecord): Pick<PickerState, 'sid' | 'fileId' | 'mode' | 't' | 'range' | 'spacing' | 'interval' | 'count' | 'sheet' | 'pins' | 'picked'> {
    const range: TimeRange | null = record.range
        ? { start: record.range.start, end: record.range.end === null ? Number.POSITIVE_INFINITY : record.range.end }
        : null;
    return {
        sid: record.sid,
        fileId: record.file ? record.file.uuid : null,
        mode: record.mode,
        t: record.t,
        range,
        spacing: record.spacing,
        interval: record.interval,
        count: record.count,
        sheet: { ...record.sheet },
        pins: [...(record.pins ?? [])],
        picked: record.picked === true && (record.pins?.length ?? 0) > 0,
    };
}

// ---------------------------------------------------------------------------------------------
// Reads and writes.
// ---------------------------------------------------------------------------------------------

export function loadRecord(sid: string, storage: PickerStorage | null = browserStorage()): PickerRecord | null {
    if (!storage || !isValidSid(sid)) return null;
    return parseRecord(storage.read(recordKey(sid)));
}

/**
 * Save with the two-tab rule (§6.4: "reconcile on `generation`"): the stored generation must not
 * be ahead of the one we last read, a foreign version is never overwritten, and the result says
 * what happened. On `saved`, the returned record carries the bumped generation the caller keeps.
 */
export function saveRecord(record: PickerRecord, storage: PickerStorage | null = browserStorage(), lastEditable?: string | null): WriteResult {
    if (!storage || !isValidSid(record.sid)) return { status: 'disabled' };
    const key = recordKey(record.sid);
    const raw = storage.read(key);
    const version = storedVersion(raw);
    if (version !== null && version > FRAMEPICKER_RECORD_VERSION) return { status: 'foreign-version', version };
    const stored = parseRecord(raw);
    // R14 (slice J, BUG-025): a stored generation ahead of ours means another writer since we last read. It is a CONFLICT
    // only when the editable content differs (the other tab moved the playhead, pinned, changed the mode…): nothing is
    // written, the caller is told, and the other tab's record stands. When only the bookkeeping differs (a Convert wrote
    // its ids -- in the other tab, or this tab's own detached wait after Back) the two are merged and the save goes on.
    // R14 (Sr review 2, MUST-FIX 2): the rule is THREE-way -- the stored content must differ from ours AND from the content
    // this tab last read or saved (`lastEditable`, the caller's `editableFingerprint` of the record it last took). A single
    // tab's press writes the record (`recordJobStart`, `recordThumbs` after the encode) and bumps the generation behind the
    // root's back; Stop, then a step, then the autosave carried a stale generation and a NEW `t` -- the 2-way rule read that
    // as another tab and undid the user's seek. The stored content equal to what we last saw is our own base: merge and save.
    if (stored && stored.generation > record.generation && isForeignEdit(stored, record, lastEditable)) return { status: 'conflict', stored };
    const base = stored ? mergeBookkeeping(record, stored) : record;
    const next: PickerRecord = { ...base, generation: Math.max(record.generation, stored?.generation ?? 0) + 1 };
    return storage.write(key, next) ? { status: 'saved', record: next } : { status: 'disabled' };
}

/** MUST-FIX 2: is the stored record's editable content someone ELSE's edit -- unlike ours, and unlike the content we last read or saved? */
function isForeignEdit(stored: PickerRecord, ours: PickerRecord, lastEditable: string | null | undefined): boolean {
    const theirs = editableFingerprint(stored);
    if (theirs === editableFingerprint(ours)) return false;
    return !(typeof lastEditable === 'string' && lastEditable !== '' && theirs === lastEditable);
}

/**
 * R14 (BUG-025): the part of a record a USER edits -- what a two-tab conflict is about: the mode, the times, the pins and
 * WHICH clip is selected. Bookkeeping (`workflowIds`, `frameTimes`, `jobs`, `savedAt`, `generation`), the refs'
 * re-addressing facts (a token that landed later) and the `staged` list (a clip added in one tab is not a fight over the
 * playhead; `mergeBookkeeping` keeps the stored list for a caller that carries none) are out.
 */
export function editableFingerprint(record: PickerRecord): string {
    return JSON.stringify({
        mode: record.mode, t: record.t, range: record.range, spacing: record.spacing, interval: record.interval, count: record.count,
        sheet: record.sheet, pins: record.pins ?? [], picked: record.picked === true, file: record.file?.uuid ?? null,
    });
}

/**
 * R14 (BUG-025/026): a save never DROPS a fact storage already holds -- workflow ids and frame times are append-only, the
 * jobs list is unioned (a job's id fills in from whichever side knows it), and a save that does not carry `staged` / `jobs`
 * at all (an older caller) keeps the stored lists.
 */
function mergeBookkeeping(ours: PickerRecord, stored: PickerRecord): PickerRecord {
    const next: PickerRecord = { ...ours, workflowIds: Array.from(new Set([...stored.workflowIds, ...ours.workflowIds])) };
    const times = parseFrameTimes({ ...(stored.frameTimes ?? {}), ...(ours.frameTimes ?? {}) });
    if (times) next.frameTimes = times; else delete next.frameTimes;
    const jobs = mergeJobs(stored.jobs, ours.jobs);
    if (jobs) next.jobs = jobs; else delete next.jobs;
    if (ours.staged === undefined && stored.staged) next.staged = stored.staged;
    // R14 (slice H, ask 10): the pictures are bookkeeping too -- unioned, ours winning per key, the newest `MAX_THUMBS` kept.
    const thumbs = mergeThumbs(stored.thumbs, ours.thumbs);
    if (thumbs) next.thumbs = thumbs; else delete next.thumbs;
    return next;
}

/** R14 (slice H, ask 10): two thumbnail maps → one; `b` wins per (file, seek) on a tie; press order and the budget hold. */
export function mergeThumbs(a: Record<string, ThumbEntry[]> | undefined, b: Record<string, ThumbEntry[]> | undefined): Record<string, ThumbEntry[]> | null {
    if (!a && !b) return null;
    return thumbBudget([...thumbEntries(a), ...thumbEntries(b)].filter((e) => !!e.file && seekKey(e.seek) !== null && Number.isFinite(e.at) && isThumbDataUrl(e.url)).map((e) => ({ ...e, seek: seekKey(e.seek) as string })));
}

/** Two job lists → one, by (fileUuid, mode, t, startedAt); a known id wins over null. Oldest first, the newest `MAX_JOBS`. */
export function mergeJobs(a: RecordedJob[] | undefined, b: RecordedJob[] | undefined): RecordedJob[] | null {
    if (!a && !b) return null;
    const byKey = new Map<string, RecordedJob>();
    for (const job of [...(a ?? []), ...(b ?? [])]) {
        const key = `${job.fileUuid}|${job.mode}|${job.t ?? ''}|${job.wire ?? ''}|${job.startedAt}`;
        const have = byKey.get(key);
        byKey.set(key, have ? { ...have, ...job, id: have.id ?? job.id, status: job.status ?? have.status, finishedAt: job.finishedAt ?? have.finishedAt } : { ...job });
    }
    const out = Array.from(byKey.values()).sort((x, y) => x.startedAt - y.startedAt).slice(-MAX_JOBS);
    return out.length ? out : null;
}

/**
 * R14 (slice J, BUG-025): what a `storage` event for this sid's key means. A `storage` event only ever fires in the
 * OTHER documents, so a stored generation ahead of ours is another tab's write:
 *   - `other-tab` -- its editable content differs from ours: the root shows "This frame picker is open in another tab…"
 *     and does NOT apply the record (the silent re-seed of the playhead was BUG-044's second half);
 *   - `bookkeeping` -- same content, more facts (the other tab converted): take the record's bookkeeping quietly;
 *   - `none` -- nothing newer (our own write echoed through a `localStorage.clear()`, or a stale event).
 * Pure: `ours` is what the root would save now (`toRecord` over its state).
 */
export function otherTabWrite(
    ours: PickerRecord,
    storage: PickerStorage | null = browserStorage(),
    lastEditable?: string | null,
): { kind: 'none' } | { kind: 'bookkeeping'; record: PickerRecord } | { kind: 'other-tab'; stored: PickerRecord } {
    const stored = loadRecord(ours.sid, storage);
    if (!stored || stored.generation <= ours.generation) return { kind: 'none' };
    // MUST-FIX 2: the same three-way reading as `saveRecord` -- the other tab writing the content we last read (it converted, it
    // did not edit) while we have edited since is its bookkeeping, not a fight over the playhead.
    return isForeignEdit(stored, ours, lastEditable) ? { kind: 'other-tab', stored } : { kind: 'bookkeeping', record: stored };
}

export function removeRecord(sid: string, storage: PickerStorage | null = browserStorage()): void {
    if (storage && isValidSid(sid)) storage.remove(recordKey(sid));
}

/**
 * §6.1/§6.5: record `sid → workflow` on `onConverted$`, BEFORE any navigation. Appends without
 * duplicates; a sid with no record yet gets a minimal one so `/downloads` can still link back.
 */
export function recordWorkflow(
    sid: string,
    workflowIds: readonly string[],
    storage: PickerStorage | null = browserStorage(),
    now: () => number = Date.now,
    fallback?: { pageSlug: string; state: PickerState; file: StagedFileRef | null; staged?: StagedFileRef[] | null },
    frameTimes?: Record<string, number> | null,
): WriteResult {
    if (!storage || !isValidSid(sid)) return { status: 'disabled' };
    const ids = workflowIds.filter((w) => typeof w === 'string' && w !== '');
    let record = loadRecord(sid, storage);
    if (!record) {
        if (!fallback) return { status: 'disabled' };
        record = toRecord(fallback.state, { pageSlug: fallback.pageSlug, file: fallback.file, staged: fallback.staged }, now);
    }
    const merged = Array.from(new Set([...record.workflowIds, ...ids]));
    // IT2 (qa N1): the user's frame time behind each written seek instant rides with the ids (merged, never replaced).
    const times = parseFrameTimes({ ...(record.frameTimes ?? {}), ...(frameTimes ?? {}) });
    const next: PickerRecord = { ...record, workflowIds: merged, savedAt: now() };
    if (times) next.frameTimes = times; else delete next.frameTimes;
    // R14 (BUG-026): each NEW id completes the oldest job of this file still waiting for one (the press wrote the job).
    const fresh = ids.filter((id) => !record!.workflowIds.includes(id));
    const jobs = record.jobs ? record.jobs.map((j) => ({ ...j })) : [];
    for (const id of fresh) {
        const pending = jobs.find((j) => j.id === null && (!fallback?.file || j.fileUuid === fallback.file.uuid)) ?? jobs.find((j) => j.id === null);
        if (pending) pending.id = id;
    }
    if (jobs.length) next.jobs = jobs;
    return saveRecord(next, storage);
}

/**
 * R14 (slice J, BUG-026 / review-6 BUG-045): Convert was PRESSED -- write the job(s) now, before the schedule POST answers,
 * so a page that leaves during the progress screen (navigate away, F5, Back) comes back to a record that knows a
 * conversion was started for this clip and this pick. `recordWorkflow` fills the id when the edge answers; a job whose
 * answer never reaches this device stays `id: null` and reads `unknown` (`jobStatus`). A sid with no record yet gets one
 * from `fallback`, as `recordWorkflow` does. Never load-bearing: `disabled` storage is reported, not thrown.
 */
export function recordJobStart(
    sid: string,
    jobs: readonly RecordedJob[],
    storage: PickerStorage | null = browserStorage(),
    now: () => number = Date.now,
    fallback?: { pageSlug: string; state: PickerState; file: StagedFileRef | null; staged?: StagedFileRef[] | null },
): WriteResult {
    if (!storage || !isValidSid(sid) || jobs.length === 0) return { status: 'disabled' };
    let record = loadRecord(sid, storage);
    if (!record) {
        if (!fallback) return { status: 'disabled' };
        record = toRecord(fallback.state, { pageSlug: fallback.pageSlug, file: fallback.file, staged: fallback.staged }, now);
    }
    const merged = mergeJobs(record.jobs, jobs.map((j) => ({ ...j })));
    const next: PickerRecord = { ...record, savedAt: now() };
    if (merged) next.jobs = merged;
    return saveRecord(next, storage);
}

/**
 * R14 (slice H, ask 10): Convert was pressed for the frame(s) of `fileUuid` whose pictures these are (`{ seek: url }`, the seek
 * instant the wire carries) -- written on the record under that file, in press order (`now()`). Called AFTER `recordJobStart`
 * (the encode is async);
 * a sid with no record yet gets one from `fallback`, as the other writers do. Oversized or malformed pictures are dropped
 * one by one (`isThumbDataUrl`); with nothing left to write the storage is untouched. Never load-bearing.
 */
export function recordThumbs(
    sid: string,
    fileUuid: string,
    thumbs: Record<string, string>,
    storage: PickerStorage | null = browserStorage(),
    now: () => number = Date.now,
    fallback?: { pageSlug: string; state: PickerState; file: StagedFileRef | null; staged?: StagedFileRef[] | null },
): WriteResult {
    if (!storage || !isValidSid(sid) || typeof fileUuid !== 'string' || fileUuid === '') return { status: 'disabled' };
    const at = now();
    const clean = parseThumbs({ [fileUuid]: Object.entries(thumbs ?? {}).map(([seek, url]) => ({ seek, url, at })) });
    if (!clean) return { status: 'disabled' };
    let record = loadRecord(sid, storage);
    if (!record) {
        if (!fallback) return { status: 'disabled' };
        record = toRecord(fallback.state, { pageSlug: fallback.pageSlug, file: fallback.file, staged: fallback.staged }, now);
    }
    const merged = mergeThumbs(record.thumbs, clean);
    const next: PickerRecord = { ...record, savedAt: now() };
    if (merged) next.thumbs = merged;
    return saveRecord(next, storage);
}

/**
 * R14 (slice H, ask 10): the picture the picker took for the job that asked ffmpeg for `seek` (the request's
 * `screenshotTime` or the note's `t=`), as a JPEG data URL -- or null when the record has none for it (a sequence, a
 * sheet, an older record, a foreign seek). The /downloads card shows it until the edge's own thumbnail answers.
 */
export function thumbFor(record: Pick<PickerRecord, 'thumbs'> | null | undefined, seek: number | string | null | undefined, fileUuid?: string | null): string | null {
    const key = seekKey(seek);
    if (key === null || !record?.thumbs) return null;
    // With the file given (the card resolves `jobs[].fileUuid`), that file's picture; without it, the newest at that seek across files.
    const pool = typeof fileUuid === 'string' && fileUuid !== '' ? thumbEntries({ [fileUuid]: record.thumbs[fileUuid] ?? [] }) : thumbEntries(record.thumbs);
    let best: FiledThumb | null = null;
    for (const e of pool) if (seekKey(e.seek) === key && isThumbDataUrl(e.url) && (!best || e.at >= best.at)) best = e;
    return best ? best.url : null;
}

/** R14 (BUG-026): the jobs a record knows, oldest first; `fileUuid` narrows them to one clip. */
/**
 * R14Q-56: is this job still guarding a re-press at `now`? A lifetime job: until it is marked failed, and at most `JOB_LIFETIME_MS` from the
 * press (done keeps the sentence until the cap). A job from before the rule: `withinMs` (the 60 s rule).
 * Editor re-open wave B (owner ruling R14Q-56 stands): a job whose id came back and has no verdict yet keeps the guard to the cap -- a
 * running job must not be schedulable twice; a card that never settles is covered by the notice's "Convert it again" key.
 */
export function jobGuards(job: RecordedJob, now: number, withinMs: number = JOB_PENDING_MS): boolean {
    if (job.guard !== 'lifetime') return now - job.startedAt <= withinMs;
    if (job.status === 'failed') return false;
    // Sr review 8 SHOULD 1: the lifetime rule is for a job the edge HAS (its id came back) -- a running job cannot be scheduled twice. A
    // press whose schedule answer never reached this device is not one; it keeps the 60 s window, or "it is on Downloads" would be said
    // for ten minutes over no card.
    if (job.id === null) return now - job.startedAt <= withinMs;
    return now - job.startedAt <= JOB_LIFETIME_MS;
}

/** R14Q-56: the edge reported the job(s) done or failed -- marked on the record so the guard reads the truth after Back. */
export function recordJobFinish(
    sid: string,
    workflowIds: readonly string[],
    status: 'done' | 'failed',
    storage: PickerStorage | null = browserStorage(),
    now: () => number = Date.now,
): WriteResult {
    if (!storage || !isValidSid(sid)) return { status: 'disabled' };
    const ids = new Set(workflowIds.filter((w) => typeof w === 'string' && w !== ''));
    if (ids.size === 0) return { status: 'disabled' };
    const record = loadRecord(sid, storage);
    if (!record?.jobs?.some((j) => j.id !== null && ids.has(j.id))) return { status: 'disabled' };
    const at = now();
    const jobs = record.jobs.map((j) => (j.id !== null && ids.has(j.id) ? { ...j, status, finishedAt: at } : { ...j }));
    return saveRecord({ ...record, jobs, savedAt: at }, storage);
}

export function jobsOf(record: Pick<PickerRecord, 'jobs'> | null | undefined, fileUuid?: string | null): RecordedJob[] {
    const all = record?.jobs ?? [];
    return fileUuid ? all.filter((j) => j.fileUuid === fileUuid) : [...all];
}

/** R14 (BUG-026): `sent` (the edge has it, the card is on /downloads), `pending` (the answer is still owed), `unknown` (it never came here). */
export function jobStatus(job: RecordedJob, now: number): JobStatus {
    if (job.id) return 'sent';
    return now - job.startedAt < JOB_PENDING_MS ? 'pending' : 'unknown';
}

/**
 * R14 (BUG-026): the newest job of a clip and its status -- what the Files row / status line reads on return ("Done · open
 * in Downloads" for `sent`; a "still being sent" line for `pending`; for `unknown` an honest "may be on Downloads"). Null
 * when this sid never converted the clip.
 */
export function latestJobFor(record: Pick<PickerRecord, 'jobs'> | null | undefined, fileUuid: string | null | undefined, now: number): { job: RecordedJob; status: JobStatus } | null {
    const jobs = jobsOf(record, fileUuid);
    if (!jobs.length) return null;
    const job = jobs.reduce((a, b) => (b.startedAt >= a.startedAt ? b : a));
    return { job, status: jobStatus(job, now) };
}

/**
 * R14 (BUG-026): is this exact pick (clip + mode + frame time for ONE) already a job of this sid, started within `withinMs`?
 * BUG-045's duplicate: "the editor looks idle, pressing Convert again is the natural move" -- the root can say the frame
 * is already on /downloads instead of making a second identical card. Pure; the root decides what to do with it.
 */
export function sameJobOf(
    record: Pick<PickerRecord, 'jobs'> | null | undefined,
    pick: { fileUuid: string; mode: PickerMode; t?: number | null; wire?: string | null },
    now: number,
    withinMs: number = JOB_PENDING_MS,
): RecordedJob | null {
    const jobs = jobsOf(record, pick.fileUuid).filter((j) => j.mode === pick.mode && jobGuards(j, now, withinMs));
    // ONE frame: the user's exact time is on the job -- the fingerprint since BUG-026, and the one the restore path keeps (after Back the
    // timeline is provisional for a moment, so a wire derived then would not equal the wire the press wrote; measured, picker-restore).
    // Editor re-open T3b: AND the output settings -- when both sides carry a full fingerprint (`jobFingerprintOf`), they must agree on
    // everything but the seek key (`t` already decided the frame), so a re-edit that changed quality / size / format is a new job.
    if (pick.mode === 'ONE') {
        const t = pick.t;
        if (typeof t !== 'number' || !Number.isFinite(t)) return null;
        return jobs.filter((j) => typeof j.t === 'number' && Math.abs(j.t - t) < PIN_EPSILON && sameOneFrameOutput(j.wire, pick.wire)).pop() ?? null;
    }
    // R14U-41 (UX pass 7): Several / Sheet -- the fingerprint is THE WIRE (the frame-selection keys the press would send) when both sides
    // carry one; records written before the wire was kept fall back to the mode rule. T3b: a full fingerprint on one side and a
    // selection-only wire (an older record, or an older press site) on the other compare on the selection keys -- the old rule.
    if (pick.wire && jobs.some((j) => typeof j.wire === 'string')) return jobs.filter((j) => sameWire(j.wire, pick.wire!)).pop() ?? null;
    return jobs.pop() ?? null;
}

/** Editor re-open T5 (R-D): the xconfig `videoToImageFrameSelection` value of a Picked-frames job -- `picker-state.ts`'s, re-exported. */
export { PICKED_FRAMES_OPTION } from './picker-state';

/**
 * Editor re-open T5: the `screenshotTimes` wire -- ONE comma-joined string of `<seconds>::SECONDS` entries (every Options V2 value is a
 * string; the edge sorts, de-dupes and clamps). `encode` is `picker-state.ts#encodeSeconds` over the seek instant of each pin.
 */
export function screenshotTimesWire(seeks: readonly string[]): string {
    return seeks.filter((s) => typeof s === 'string' && s !== '').join(',');
}

/**
 * Editor re-open T3b: the marker pair that opens a FULL job fingerprint. A wire without it is the R14U-41 selection-only wire
 * (`export/convert.ts#wireKeyOf`): the same `[[key, value], …]` JSON, frame-selection keys only.
 */
export const JOB_FINGERPRINT_MARKER: readonly [string, string] = ['@fp', '2'];

/** Summary keys that describe the SOURCE or the page, not the output the user asked for -- never part of the fingerprint. */
const NOT_OUTPUT_KEYS: ReadonlySet<string> = new Set(['process', 'fromMedia', 'toMedia', 'fromExtension', 'uploadedFileExtensions']);

/** The frame-selection keys -- the ones `wireKeyOf` keeps (`videoToImageFrameSelection`, every `screenshot*`). */
function isSelectionKey(key: string): boolean {
    return key === 'videoToImageFrameSelection' || key.startsWith('screenshot');
}

function wireValue(v: unknown): string | null {
    if (v === undefined || v === null) return null;
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/**
 * Editor re-open T3b: the picker job's fingerprint for EVERY mode (One frame included) -- the frame-selection keys the press
 * writes (`writes`, `toOptionWrites`'s answer) PLUS the output option summary the request carries (quality preset / target size,
 * resolution, format, sheet layout, orientation: every summary key but the source-describing ones). `writes` wins over the
 * summary, and a frame-selection key found only in the summary is dropped (it is the PREVIOUS press's write, still in the
 * context). Sorted, so two presses of the identical request produce the identical string. Pure.
 */
export function jobFingerprintOf(writes: Record<string, unknown>, summary: Record<string, unknown> | null | undefined): string {
    const merged = new Map<string, string | null>();
    for (const [k, v] of Object.entries(summary ?? {})) {
        if (NOT_OUTPUT_KEYS.has(k) || isSelectionKey(k)) continue;
        merged.set(k, wireValue(v));
    }
    for (const [k, v] of Object.entries(writes ?? {})) merged.set(k, wireValue(v));
    const pairs = [...merged.keys()].sort().map((k) => [k, merged.get(k) ?? null]);
    return JSON.stringify([JOB_FINGERPRINT_MARKER, ...pairs]);
}

/**
 * Review 7 JE-01: a recorded job's own options, as a summary (`"true"`/`"false"` back to booleans), from its full fingerprint --
 * what a card's "Pick another frame" restores. Null for a job with no full fingerprint (older records, per-pin ONE jobs).
 */
export function jobSummaryOf(job: Pick<RecordedJob, 'wire'> | null | undefined): Record<string, string | boolean | null> | null {
    const pairs = fingerprintPairs(job?.wire);
    if (!pairs) return null;
    const out: Record<string, string | boolean | null> = {};
    for (const [k, v] of pairs) out[k] = v === 'true' ? true : v === 'false' ? false : v;
    return out;
}

/** The pairs of a full fingerprint (marker removed), or null for a selection-only / unreadable wire. */
function fingerprintPairs(wire: string | null | undefined): [string, string | null][] | null {
    if (typeof wire !== 'string' || wire === '') return null;
    try {
        const parsed = JSON.parse(wire);
        if (!Array.isArray(parsed) || parsed.length === 0) return null;
        const head = parsed[0];
        if (!Array.isArray(head) || head[0] !== JOB_FINGERPRINT_MARKER[0] || head[1] !== JOB_FINGERPRINT_MARKER[1]) return null;
        return parsed.slice(1).filter((p: unknown): p is [string, string | null] => Array.isArray(p) && typeof p[0] === 'string');
    } catch {
        return null;
    }
}

/** A full fingerprint read as the selection-only wire `wireKeyOf` would have written for the same press. */
function selectionWireOf(pairs: [string, string | null][]): string {
    return JSON.stringify(pairs.filter(([k]) => isSelectionKey(k)));
}

/** Several / Sheet: equal full fingerprints; a full and a selection-only wire compare on the selection keys (the old rule). */
function sameWire(jobWire: string | undefined, pickWire: string): boolean {
    if (typeof jobWire !== 'string') return false;
    const a = fingerprintPairs(jobWire);
    const b = fingerprintPairs(pickWire);
    if (a && b) return jobWire === pickWire;
    if (a) return selectionWireOf(a) === pickWire;
    if (b) return jobWire === selectionWireOf(b);
    return jobWire === pickWire;
}

/**
 * One frame: `t` decided the frame; the output settings must agree when BOTH sides carry a full fingerprint (the seek key is left
 * out -- it is `t` under a timeline that may still be provisional after Back). Anything less (a legacy record, a press site that
 * sends no fingerprint) is the old rule: `t` alone.
 */
function sameOneFrameOutput(jobWire: string | undefined, pickWire: string | null | undefined): boolean {
    const a = fingerprintPairs(jobWire);
    const b = fingerprintPairs(pickWire);
    if (!a || !b) return true;
    const withoutSeek = (pairs: [string, string | null][]) => JSON.stringify(pairs.filter(([k]) => k !== 'screenshotTime'));
    return withoutSeek(a) === withoutSeek(b);
}

/**
 * R14 (Sr review 1, NIT 13): pins and range bounds stored ms-rounded by a pre-BUG-001 build (`1.433` for frame 43 at
 * 30 fps) land a frame early under the containing-frame rule; once the clip's REAL rate is known each one is re-snapped
 * to the NEAREST frame start (a value already on a frame start is unchanged; two pins that meet become one). A
 * provisional timeline is not a rate: nothing is touched. Pure; returns the same arrays when nothing moved.
 */
export function resnapPicks(pins: readonly number[], range: TimeRange | null, tl: Timeline | null): { pins: number[]; range: TimeRange | null; moved: boolean } {
    if (!tl || tl.provisional || !(tl.fps > 0)) return { pins: [...pins], range: range ? { ...range } : null, moved: false };
    const nearest = (t: number) => frameStart(Math.round(t * tl.fps), tl);
    let moved = false;
    const snappedPins: number[] = [];
    for (const p of pins) {
        const s = nearest(p);
        if (Math.abs(s - p) > 1e-9) moved = true;
        if (!snappedPins.some((q) => Math.abs(q - s) < PIN_EPSILON)) snappedPins.push(s);
    }
    if (snappedPins.length !== pins.length) moved = true;
    snappedPins.sort((a, b) => a - b);
    let nextRange: TimeRange | null = range ? { ...range } : null;
    if (nextRange) {
        const start = nearest(nextRange.start);
        if (Math.abs(start - nextRange.start) > 1e-9) { nextRange.start = start; moved = true; }
        if (Number.isFinite(nextRange.end)) {
            const end = nearest(nextRange.end);
            if (Math.abs(end - nextRange.end) > 1e-9 && end > nextRange.start) { nextRange.end = end; moved = true; }
        }
    }
    return { pins: snappedPins, range: nextRange, moved };
}

/** NIT 13 over a whole record (the load path): the same re-snap of `pins` and `range`; the record itself when nothing moved. */
export function resnapRecord(record: PickerRecord, tl: Timeline | null): PickerRecord {
    const range: TimeRange | null = record.range ? { start: record.range.start, end: record.range.end === null ? Number.POSITIVE_INFINITY : record.range.end } : null;
    const snapped = resnapPicks(record.pins ?? [], range, tl);
    if (!snapped.moved) return record;
    return {
        ...record,
        pins: snapped.pins,
        picked: record.picked === true && snapped.pins.length > 0,
        range: snapped.range ? { start: snapped.range.start, end: Number.isFinite(snapped.range.end) ? snapped.range.end : null } : null,
    };
}

/**
 * IT2 (qa N1): the key a written `screenshotTime` is remembered under -- its seconds rounded to the millisecond
 * `encodeSeconds` carries, as a plain decimal string ("1.55", "7.266", "0"). Takes the number, the bare decimal, or
 * the wire form `"1.55::SECONDS"` (`FileUpload.options.screenshotTime`, the card's raw). `null` for anything else.
 */
export function seekKey(seek: number | string | null | undefined): string | null {
    const raw = typeof seek === 'string' ? seek.split('::')[0].trim() : seek;
    const n = typeof raw === 'number' ? raw : raw === '' || raw === undefined || raw === null ? NaN : Number(raw);
    if (!Number.isFinite(n) || n < 0) return null;
    return String(Math.round(n * 1000) / 1000);
}

/**
 * IT2 (qa N1): the frame time the user chose for the job that asked ffmpeg for `seek` (the request's `screenshotTime`
 * or the note's `t=`), or `null` when this record does not know it (an older record, a sequence, a foreign seek).
 * The /downloads card's badge reads this over the seek instant: "Frame at 0:01.567" for a typed 1.567, not "0:01.550".
 */
export function userFrameTimeOf(record: Pick<PickerRecord, 'frameTimes'> | null | undefined, seek: number | string | null | undefined): number | null {
    const key = seekKey(seek);
    if (key === null || !record?.frameTimes) return null;
    const t = record.frameTimes[key];
    return typeof t === 'number' && Number.isFinite(t) && t >= 0 ? t : null;
}

/** `/downloads` "Pick another frame": which sid produced this workflow? */
export function findRecordByWorkflow(workflowId: string, storage: PickerStorage | null = browserStorage()): PickerRecord | null {
    if (!storage || !workflowId) return null;
    for (const key of storage.keys()) {
        const sid = sidFromKey(key);
        if (!sid) continue;
        const record = parseRecord(storage.read(key));
        if (record && record.workflowIds.includes(workflowId)) return record;
    }
    return null;
}

export function setLatest(pageSlug: string, sid: string, storage: PickerStorage | null = browserStorage()): boolean {
    if (!storage || !pageSlug || !isValidSid(sid)) return false;
    return storage.write(latestKey(pageSlug), sid);
}

export function getLatest(pageSlug: string, storage: PickerStorage | null = browserStorage()): string | null {
    if (!storage || !pageSlug) return null;
    const v = storage.read(latestKey(pageSlug));
    return isValidSid(v) ? v : null;
}

/**
 * §6.4: "swept 8 h after `savedAt` on open using `keySet('FRAMEPICKER-')` with a key-shape filter".
 * Removes expired records AND unreadable blobs under a record-shaped key (garbage never
 * accumulates); leaves `LATEST-*` alone, then drops a LATEST pointer whose record is gone.
 * Returns the removed record keys.
 */
export function sweep(storage: PickerStorage | null = browserStorage(), now: () => number = Date.now, ttlMs: number = FRAMEPICKER_TTL_MS): string[] {
    if (!storage) return [];
    const removed: string[] = [];
    const cutoff = now() - ttlMs;
    const keys = storage.keys();
    for (const key of keys) {
        const sid = sidFromKey(key);
        if (!sid) continue;
        const record = parseRecord(storage.read(key));
        if (!record || record.savedAt < cutoff) {
            storage.remove(key);
            removed.push(key);
        }
    }
    const latestHead = `${FRAMEPICKER_PREFIX}-${LATEST_PREFIX}`;
    for (const key of keys) {
        if (!key.startsWith(latestHead)) continue;
        const sid = storage.read(key);
        if (!isValidSid(sid) || storage.read(recordKey(sid)) === null) storage.remove(key);
    }
    return removed;
}
